<template>
  <v-dialog :value="value" max-width="1100" scrollable @input="onDialogInput">
    <v-card class="run-compare-dialog">
      <v-card-title class="px-6">
        <h5 class="primary-color mb-0">Compare runs</h5>
        <v-chip
          v-if="comparison"
          x-small
          outlined
          label
          class="ml-3"
          :color="comparison.same_source ? 'success' : 'warning'"
          :title="
            comparison.same_source
              ? 'Both runs analyse the same video'
              : 'The runs analyse different videos'
          "
        >
          {{ comparison.same_source ? 'Same video' : 'Different videos' }}
        </v-chip>
        <v-spacer />
        <v-btn icon aria-label="Close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 py-2">
        <v-skeleton-loader v-if="loading" type="table-row@6" />

        <v-alert v-else-if="error" type="error" outlined dense>
          <div class="d-flex align-center">
            <span>{{ error }}</span>
            <v-spacer />
            <v-btn small outlined color="error" @click="load">Retry</v-btn>
          </div>
        </v-alert>

        <template v-else-if="comparison">
          <!-- Run identity, side by side -->
          <v-row dense class="run-compare-dialog__briefs">
            <v-col v-for="side in sides" :key="side.key" cols="12" md="6">
              <v-card outlined class="run-compare-dialog__brief">
                <div class="run-compare-dialog__brief-title">
                  <span class="run-compare-dialog__side">{{ side.key.toUpperCase() }}</span>
                  <strong class="ml-2">{{ runHeadline(side.run) || 'Run' }}</strong>
                  <v-spacer />
                  <RunStatusChip :status="side.run && side.run.status" x-small />
                </div>
                <div class="caption grey--text text--darken-1">{{ briefCaption(side.run) }}</div>
                <RunVersionChips
                  :run="side.run"
                  :source-type="side.run && side.run.source_type"
                  dense
                  class="mt-1"
                />
              </v-card>
            </v-col>
          </v-row>

          <!-- Funnel -->
          <div class="run-compare-dialog__section">Funnel</div>
          <v-simple-table dense class="run-compare-dialog__table">
            <template #default>
              <thead>
                <tr>
                  <th>Stage</th>
                  <th class="text-right">A</th>
                  <th class="text-right">B</th>
                  <th class="text-right">Delta</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in funnelRows" :key="row.key" class="run-compare-dialog__funnel-row">
                  <td>{{ row.label }}</td>
                  <td class="text-right">{{ fmt(row.a) }}</td>
                  <td class="text-right">{{ fmt(row.b) }}</td>
                  <td class="text-right font-weight-bold" :class="deltaClass(row.delta)">
                    {{ fmtDelta(row.delta) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Alerts by gesture -->
          <div class="run-compare-dialog__section">Alerts by gesture</div>
          <p v-if="!alertsByGesture.length" class="caption grey--text">No alerts in either run.</p>
          <v-simple-table v-else dense class="run-compare-dialog__table run-compare-dialog__alerts">
            <template #default>
              <thead>
                <tr>
                  <th>Gesture</th>
                  <th class="text-right">A</th>
                  <th class="text-right">B</th>
                  <th class="text-right">Delta</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in alertsByGesture" :key="row.gesture">
                  <td>{{ row.gesture }}</td>
                  <td class="text-right">{{ fmt(row.a) }}</td>
                  <td class="text-right">{{ fmt(row.b) }}</td>
                  <td class="text-right font-weight-bold" :class="deltaClass(row.delta)">
                    {{ fmtDelta(row.delta) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Ideas -->
          <div class="run-compare-dialog__section">Subtracting ideas</div>
          <v-row dense class="run-compare-dialog__ideas">
            <v-col cols="12" md="6">
              <div class="caption font-weight-bold">Only in A ({{ ideas.only_in_a.length }})</div>
              <p v-if="!ideas.only_in_a.length" class="caption grey--text">—</p>
              <ul v-else class="run-compare-dialog__idea-list">
                <li v-for="(idea, index) in ideas.only_in_a" :key="`a-${index}`">
                  {{ ideaText(idea) }}
                </li>
              </ul>
            </v-col>
            <v-col cols="12" md="6">
              <div class="caption font-weight-bold">Only in B ({{ ideas.only_in_b.length }})</div>
              <p v-if="!ideas.only_in_b.length" class="caption grey--text">—</p>
              <ul v-else class="run-compare-dialog__idea-list">
                <li v-for="(idea, index) in ideas.only_in_b" :key="`b-${index}`">
                  {{ ideaText(idea) }}
                </li>
              </ul>
            </v-col>
          </v-row>
          <div class="caption grey--text mb-3">{{ ideas.both }} ideas subtract in both runs</div>

          <!-- Config and engine diff -->
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="run-compare-dialog__section">Configuration changes</div>
              <p v-if="!configDiff.length" class="caption grey--text">Same configuration.</p>
              <v-simple-table
                v-else
                dense
                class="run-compare-dialog__table run-compare-dialog__config"
              >
                <template #default>
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>A</th>
                      <th>B</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in configDiff" :key="row.path">
                      <td class="run-compare-dialog__mono">{{ row.path }}</td>
                      <td>{{ fmtValue(row.a) }}</td>
                      <td>{{ fmtValue(row.b) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
            <v-col cols="12" md="6">
              <div class="run-compare-dialog__section">Engine changes</div>
              <p v-if="!engineDiff.length" class="caption grey--text">Same engines and build.</p>
              <v-simple-table
                v-else
                dense
                class="run-compare-dialog__table run-compare-dialog__engines"
              >
                <template #default>
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>A</th>
                      <th>B</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in engineDiff" :key="row.key">
                      <td class="run-compare-dialog__mono">{{ row.key }}</td>
                      <td>{{ fmtValue(row.a) }}</td>
                      <td>{{ fmtValue(row.b) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>

          <!-- Timings -->
          <div class="run-compare-dialog__section">Timings</div>
          <p v-if="!timings.length" class="caption grey--text">No stage timings recorded.</p>
          <v-simple-table
            v-else
            dense
            class="run-compare-dialog__table run-compare-dialog__timings"
          >
            <template #default>
              <thead>
                <tr>
                  <th>Stage</th>
                  <th class="text-right">A</th>
                  <th class="text-right">B</th>
                  <th class="text-right">Delta</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in timings" :key="row.stage">
                  <td>{{ row.stage }}</td>
                  <td class="text-right">{{ fmtMs(row.a_ms) }}</td>
                  <td class="text-right">{{ fmtMs(row.b_ms) }}</td>
                  <td class="text-right" :class="deltaClass(msDelta(row), true)">
                    {{ fmtMsDelta(row) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6 py-3">
        <span class="caption grey--text text--darken-2 run-compare-dialog__ids">
          <span class="run-compare-dialog__mono">{{ a }}</span> vs
          <span class="run-compare-dialog__mono">{{ b }}</span>
        </span>
        <v-spacer />
        <v-btn text class="cancel-modal" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import RunStatusChip from '@/components/analysis/runs/RunStatusChip.vue';
import RunVersionChips from '@/components/analysis/runs/RunVersionChips.vue';
import { formatAge } from '@/components/queue/queueFormat';
import AnalysisRunsService from '@/services/AnalysisRunsService';
import { runHeadline } from '@/utils/runLabels';

const FUNNEL_STAGES = [
  { key: 'episodes', label: 'Gesture episodes' },
  { key: 'alerts', label: 'Alerts' },
  { key: 'ideas', label: 'Ideas' },
  { key: 'ideas_subtracting', label: 'Subtracting ideas' },
];

/**
 * Minimal run comparison (M4 contract 9, `GET /analysis-runs/compare?a=&b=`): the two runs'
 * identity, the funnel with deltas, alerts by gesture, subtracting ideas present in only one run,
 * configuration and engine differences and stage timings.
 */
export default {
  name: 'RunCompareDialog',

  components: { RunStatusChip, RunVersionChips },

  props: {
    value: { type: Boolean, default: false },
    /** Run id shown on the left. */
    a: { type: String, default: null },
    /** Run id shown on the right. */
    b: { type: String, default: null },
  },

  data() {
    return { comparison: null, loading: false, error: '' };
  },

  computed: {
    sides() {
      return [
        { key: 'a', run: this.comparison?.a || null },
        { key: 'b', run: this.comparison?.b || null },
      ];
    },
    funnelRows() {
      const funnel = this.comparison?.funnel || {};
      const a = funnel.a || {};
      const b = funnel.b || {};
      const delta = funnel.delta || {};
      return FUNNEL_STAGES.map((stage) => {
        const va = a[stage.key] ?? null;
        const vb = b[stage.key] ?? null;
        const provided = delta[stage.key];
        return {
          ...stage,
          a: va,
          b: vb,
          delta: provided ?? (va === null || vb === null ? null : vb - va),
        };
      });
    },
    alertsByGesture() {
      return (this.comparison?.alerts_by_gesture || []).map((row) => ({
        gesture: row.gesture,
        a: row.a ?? 0,
        b: row.b ?? 0,
        delta: row.delta ?? (row.b ?? 0) - (row.a ?? 0),
      }));
    },
    ideas() {
      const ideas = this.comparison?.ideas || {};
      return {
        only_in_a: Array.isArray(ideas.only_in_a) ? ideas.only_in_a : [],
        only_in_b: Array.isArray(ideas.only_in_b) ? ideas.only_in_b : [],
        both: Number(ideas.both ?? 0),
      };
    },
    configDiff() {
      return Array.isArray(this.comparison?.config_diff) ? this.comparison.config_diff : [];
    },
    engineDiff() {
      return Array.isArray(this.comparison?.engine_diff) ? this.comparison.engine_diff : [];
    },
    timings() {
      return Array.isArray(this.comparison?.timings) ? this.comparison.timings : [];
    },
  },

  watch: {
    value(open) {
      if (open) this.load();
    },
    a() {
      if (this.value) this.load();
    },
    b() {
      if (this.value) this.load();
    },
  },

  created() {
    if (this.value) this.load();
  },

  methods: {
    runHeadline,

    async load() {
      if (!this.a || !this.b) {
        this.comparison = null;
        this.error = 'Select two runs to compare.';
        return;
      }
      this.loading = true;
      this.error = '';
      try {
        const response = await AnalysisRunsService.compare(this.a, this.b);
        this.comparison = response?.data?.data ?? response?.data ?? null;
        if (!this.comparison) this.error = 'Empty comparison.';
      } catch (error) {
        const data = error?.response?.data;
        this.error = data?.message || data?.error || 'Could not compare the runs.';
      } finally {
        this.loading = false;
      }
    },

    briefCaption(run) {
      if (!run) return '';
      const parts = [];
      if (run.source_label) parts.push(run.source_label);
      else if (run.source_type && run.source_id != null)
        parts.push(`${run.source_type} #${run.source_id}`);
      if (run.batch) parts.push(`batch ${run.batch}`);
      if (run.requested_by) parts.push(`by ${run.requested_by}`);
      const age = formatAge(run.finished_at || run.started_at || run.enqueued_at);
      if (age) parts.push(age);
      return parts.join(' · ');
    },

    ideaText(idea) {
      if (idea && typeof idea === 'object') {
        return idea.text || idea.idea || idea.label || idea.id || JSON.stringify(idea);
      }
      return String(idea);
    },

    fmt(value) {
      return value === null || value === undefined ? '—' : String(value);
    },

    fmtValue(value) {
      if (value === null || value === undefined) return '—';
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
    },

    fmtDelta(delta) {
      if (delta === null || delta === undefined) return '—';
      const n = Number(delta);
      if (!Number.isFinite(n)) return String(delta);
      return n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '0';
    },

    deltaClass(delta, lowerIsBetter = false) {
      const n = Number(delta);
      if (!Number.isFinite(n) || n === 0) return '';
      const better = lowerIsBetter ? n < 0 : n > 0;
      return better ? 'success--text' : 'error--text';
    },

    fmtMs(ms) {
      if (ms === null || ms === undefined) return '—';
      const n = Number(ms);
      if (!Number.isFinite(n)) return '—';
      if (n < 1000) return `${Math.round(n)} ms`;
      const total = Math.round(n / 1000);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return m ? `${m} m ${String(s).padStart(2, '0')} s` : `${s} s`;
    },

    msDelta(row) {
      if (row.a_ms == null || row.b_ms == null) return null;
      return Number(row.b_ms) - Number(row.a_ms);
    },

    fmtMsDelta(row) {
      const delta = this.msDelta(row);
      if (delta === null) return '—';
      const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
      return `${sign}${this.fmtMs(Math.abs(delta))}`;
    },

    onDialogInput(open) {
      if (!open) this.close();
    },

    close() {
      this.$emit('input', false);
    },
  },
};
</script>

<style scoped>
.run-compare-dialog__brief {
  padding: 10px 12px;
  border-radius: 8px !important;
}
.run-compare-dialog__brief-title {
  display: flex;
  align-items: center;
}
.run-compare-dialog__side {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #1976d2;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.run-compare-dialog__section {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #757575;
  margin: 16px 0 6px;
}
.run-compare-dialog__table {
  background: transparent !important;
}
.run-compare-dialog__idea-list {
  padding-left: 18px;
  font-size: 12px;
}
.run-compare-dialog__mono {
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
}
</style>
